import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { t } from 'i18next';
import { useCallback, useEffect, useRef, useState } from 'react';
import { useEnv } from '../../../context/env-context';
import { useHandlers, useSite } from '../../../context/page-context';
import type { I18nPrefix } from '../../../i18n/prefix';
import { isServerError } from '../../../utils/errors';
import type { StorefrontOptionsResponse } from '../api';
import type { DisplayOptions, SparkOptions } from '../types/spark-options';
import { hasInvalidCheckoutElements } from '../utils/checkout-element-validation';
import { hasInvalidLocaleValue } from '../utils/locale-validation';
import { decodeSparkOptions, defaultSparkOptions, encodeSparkOptions } from '../utils/storage';
import { stripVersionGatedCheckoutElements } from '../utils/version-gated-elements';
import { type UseSiteSettingsDraft, useSiteSettingsDraft } from './use-site-settings-draft';

const PAGE = 'features:storefront_options.pages.storefront_options_page' satisfies I18nPrefix;

const QK_STOREFRONT_OPTIONS = (siteId: string, siteEnv: string) =>
  ['storefront-options', siteId, siteEnv] as const;

export type UseStorefrontOptions = {
  options: SparkOptions;
  /** Site settings saved alongside the draft rather than in it. See {@link useSiteSettingsDraft}. */
  siteSettings: UseSiteSettingsDraft;
  set: <K extends keyof SparkOptions>(key: K, value: SparkOptions[K]) => void;
  setDisplay: <K extends keyof DisplayOptions>(key: K, value: DisplayOptions[K]) => void;
  setOptions: (updater: (prev: SparkOptions) => SparkOptions) => void;
  discardChanges: () => void;
  resetToDefaults: () => void;
  deleteSettings: () => void;
  saveAndPublish: () => void;
  publishedAt: string | null;
  hasPublished: boolean;
  hasUnpublishedChanges: boolean;
  canResetToDefaults: boolean;
  canDelete: boolean;
  isDirty: boolean;
  isSaving: boolean;
  isDeleting: boolean;
  isBusy: boolean;
  showValidationErrors: boolean;
  isLoading: boolean;
  isError: boolean;
  refetch: () => void;
};

export function useStorefrontOptions(): UseStorefrontOptions {
  const site = useSite();
  const env = useEnv();
  const { apis, notificationHandler } = useHandlers();
  const queryClient = useQueryClient();
  const siteSettings = useSiteSettingsDraft();
  const {
    save: saveSiteSettings,
    reset: resetSiteSettings,
    resetToDefaults: resetSiteSettingsToDefaults,
    isDirty: isSiteSettingsDirty,
    isAtDefaults: isSiteSettingsAtDefaults,
    isSaving: isSavingSiteSettings,
  } = siteSettings;

  const queryKey = QK_STOREFRONT_OPTIONS(site.siteId, site.siteEnv);

  const query = useQuery({
    queryKey,
    queryFn: () => apis.storefrontOptions.get(site.siteId, site.siteEnv),
  });
  const { refetch: refetchConfig } = query;

  const [draft, setDraft] = useState<SparkOptions | null>(null);
  const [isDirty, setIsDirty] = useState<boolean>(false);
  const [showValidationErrors, setShowValidationErrors] = useState<boolean>(false);

  const draftRef = useRef<SparkOptions | null>(draft);
  draftRef.current = draft;

  const applyConfig = useCallback((config: StorefrontOptionsResponse) => {
    const next = decodeSparkOptions({ value: config.value ?? '{}' });
    draftRef.current = next;
    setDraft(next);
    setIsDirty(false);
    setShowValidationErrors(false);
  }, []);

  const hydratedKeyRef = useRef<string | null>(null);
  useEffect(() => {
    const key = `${site.siteId}:${site.siteEnv}`;
    if (!query.data || hydratedKeyRef.current === key) return;

    hydratedKeyRef.current = key;
    applyConfig(query.data);
  }, [query.data, applyConfig, site.siteId, site.siteEnv]);

  const hasUnpublishedChanges = isDirty || query.data?.dirty === true;

  const hasValidationErrors =
    draft !== null &&
    (hasInvalidCheckoutElements(draft.checkoutCustomElements) ||
      hasInvalidLocaleValue(draft.translations));

  const persist = useCallback((next: SparkOptions) => {
    draftRef.current = next;
    setDraft(next);
    setIsDirty(true);
  }, []);

  const set = useCallback<UseStorefrontOptions['set']>(
    (field, value) => {
      if (!draftRef.current) return;
      persist({ ...draftRef.current, [field]: value });
    },
    [persist],
  );

  const setDisplay = useCallback<UseStorefrontOptions['setDisplay']>(
    (field, value) => {
      if (!draftRef.current) return;
      persist({
        ...draftRef.current,
        display: { ...draftRef.current.display, [field]: value },
      });
    },
    [persist],
  );

  const setOptions = useCallback<UseStorefrontOptions['setOptions']>(
    (updater) => {
      if (!draftRef.current) return;
      persist(updater(draftRef.current));
    },
    [persist],
  );

  const settleDraft = useCallback((sentValue: string) => {
    const current = draftRef.current;
    if (!current || encodeSparkOptions(current).value !== sentValue) return;

    setIsDirty(false);
  }, []);

  const notifyFailure = useCallback(
    (error: unknown, message: string) => {
      if (isServerError(error)) return;
      notificationHandler.notify(message, undefined, 'error');
    },
    [notificationHandler],
  );

  const saveAndPublishMutation = useMutation({
    mutationFn: async (value: string) => {
      await apis.storefrontOptions.update(site.siteId, site.siteEnv, { value });
      await apis.storefrontOptions.publish(site.siteId, site.siteEnv);
    },
    onSuccess: async (_result, value) => {
      await queryClient.invalidateQueries({ queryKey });
      settleDraft(value);
      setShowValidationErrors(false);
      notificationHandler.notify(t('notifications:saved'), undefined, 'success');
    },
    onError: (error) => notifyFailure(error, t(`${PAGE}.save_error`)),
  });

  const deleteMutation = useMutation({
    mutationFn: () => apis.storefrontOptions.delete(site.siteId, site.siteEnv),
    onSuccess: async () => {
      const result = await refetchConfig();
      if (result.isSuccess && result.data) {
        applyConfig(result.data);
      }
      notificationHandler.notify(t(`${PAGE}.delete_success`), undefined, 'success');
    },
    onError: (error) => notifyFailure(error, t(`${PAGE}.delete_error`)),
  });

  const { mutate: saveAndPublishMutate, isPending: isSaving } = saveAndPublishMutation;
  const { mutate: runDelete, isPending: isDeleting } = deleteMutation;
  const isBusy = isSaving || isDeleting || isSavingSiteSettings;

  const saveAndPublish = useCallback(() => {
    if (isBusy) return;

    if (hasValidationErrors) {
      setShowValidationErrors(true);
      notificationHandler.notify(t(`${PAGE}.validation_error`), undefined, 'error');
      return;
    }

    saveSiteSettings({ notify: !isDirty });

    if (!draftRef.current || !isDirty) return;

    // Persisted locally too, so settleDraft's comparison matches once the save succeeds
    const sanitizedDraft: SparkOptions = {
      ...draftRef.current,
      checkoutCustomElements: stripVersionGatedCheckoutElements(
        draftRef.current.checkoutCustomElements,
        env.sparkjs_version,
      ),
    };
    persist(sanitizedDraft);
    saveAndPublishMutate(encodeSparkOptions(sanitizedDraft).value);
  }, [
    isDirty,
    isBusy,
    hasValidationErrors,
    notificationHandler,
    saveAndPublishMutate,
    saveSiteSettings,
    persist,
    env.sparkjs_version,
  ]);

  const resetToDefaults = useCallback(() => {
    if (isBusy) return;

    resetSiteSettingsToDefaults();
    persist(defaultSparkOptions());
    setShowValidationErrors(false);
  }, [isBusy, persist, resetSiteSettingsToDefaults]);

  const isAtDefaults =
    isSiteSettingsAtDefaults &&
    draft !== null &&
    encodeSparkOptions(draft).value === encodeSparkOptions(defaultSparkOptions()).value;

  const hasStoredConfig = query.data
    ? query.data.value !== null || query.data.published_at !== null
    : false;

  const deleteSettings = useCallback(() => {
    if (isBusy || !hasStoredConfig) return;
    runDelete();
  }, [isBusy, hasStoredConfig, runDelete]);

  const discardChanges = useCallback(() => {
    if (isBusy) return;

    resetSiteSettings();
    setShowValidationErrors(false);

    if (!isDirty || !query.data) return;
    applyConfig(query.data);
  }, [isBusy, isDirty, query.data, applyConfig, resetSiteSettings]);

  const refetch = useCallback(() => {
    refetchConfig();
  }, [refetchConfig]);

  const publishedAt = query.data?.published_at ?? null;
  const hasPublished = publishedAt !== null;

  return {
    options: draft ?? defaultSparkOptions(),
    siteSettings,
    set,
    setDisplay,
    setOptions,
    discardChanges,
    resetToDefaults,
    deleteSettings,
    saveAndPublish,
    publishedAt,
    hasPublished,
    hasUnpublishedChanges,
    canResetToDefaults: !isAtDefaults,
    canDelete: hasStoredConfig,
    isDirty: isDirty || isSiteSettingsDirty,
    isSaving: isSaving || isSavingSiteSettings,
    isDeleting,
    isBusy,
    showValidationErrors,
    isLoading: query.isLoading || !draft,
    isError: query.isError,
    refetch,
  };
}
